import { useGetAllAssessmentsQuery } from '@/app/features/recruitment/assessments/assessment.api';
import { useAssignAssessmentMutation } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import type { ShortlistedApplicantsResponse } from '@/app/features/recruitment/job-applicants/job-applicants.type';
import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import useSelectionOptions from '@/hooks/useSelectionOptions';
import {
  BriefcaseIcon,
  ChevronDownIcon,
  MailIcon,
  UserCogIcon,
  XIcon,
} from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, DatePicker, Flex, Form, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

type Props = {
  employees?: ShortlistedApplicantsResponse['data']['data'];
  handleClose: () => void;
  onSuccess?: () => void;
};

export default function AssignAssessment({
  employees,
  handleClose,
  onSuccess,
}: Props) {
  const [form] = Form.useForm();
  const [currentApplicants, setCurrentApplicants] = useState(employees || []);

  const [assignAssessment, { isLoading: isAssigning }] =
    useAssignAssessmentMutation();

  const submissionDeadline = Form.useWatch('submission_deadline', form);

  const [assessmentOptions, isFetchingAssessments] = useSelectionOptions(
    useGetAllAssessmentsQuery,
    {
      labelKey: 'title',
      valueKey: 'id',
      params: { limit: 1000, status: 'active' },
    },
  );

  const handleSubmit = async (values: any) => {
    const {
      assessment_id,
      submission_time,
      review_deadline,
      submission_deadline,
      job_application_ids,
    } = values || {};
    const payload = {
      assessment_id: assessment_id,
      job_application_ids: job_application_ids,
      submission_time: dayjs(submission_time).format('HH:mm'),
      review_deadline: dayjs(review_deadline).format('YYYY-MM-DD'),
      submission_deadline: dayjs(submission_deadline).format('YYYY-MM-DD'),
    };

    if (currentApplicants.length === 0) {
      toast('error', 'Please select at least one applicant');
      return;
    }

    try {
      const res = await assignAssessment(payload).unwrap();
      if (res.success) {
        toast(
          'success',
          getErrorMessage(res, 'Assessment assigned successfully'),
        );
        onSuccess?.();
        handleClose();
      } else {
        toast('error', getErrorMessage(res, 'Failed to assign assessment'));
      }
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to assign assessment'));
    }
  };

  const handleRemove = (id: string) => {
    const updated = currentApplicants.filter((emp) => emp.id !== id);
    setCurrentApplicants(updated);
    form.setFieldValue(
      'job_application_ids',
      updated.map((emp) => emp.id),
    );
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form space-y-6"
      onFinish={handleSubmit}
    >
      <Form.Item
        hidden
        name="job_application_ids"
        initialValue={currentApplicants?.map((emp) => emp.id)}
      />
      <div className="grid grid-cols-2 gap-6 !mt-0">
        <Form.Item
          label="Assessment Title"
          name="assessment_id"
          rules={[
            {
              required: true,
              message: 'Please select assessment title',
            },
          ]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select"
            loading={isFetchingAssessments}
            options={assessmentOptions}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          />
        </Form.Item>

        <Form.Item
          label="Submission Dead Line"
          name="submission_deadline"
          rules={[
            {
              required: true,
              message: 'Please select submission deadline',
            },
          ]}
        >
          <DatePicker
            picker="date"
            format="YYYY/MM/DD"
            placeholder="YYYY/MM/DD"
            style={{ width: '100%' }}
            disabledDate={(current) =>
              current && current < dayjs().startOf('day')
            }
          />
        </Form.Item>

        <Form.Item
          label="Submission Time"
          name="submission_time"
          rules={[
            {
              required: true,
              message: 'Please select submission time',
            },
          ]}
        >
          <DatePicker
            picker="time"
            format="HH:mm"
            placeholder="HH:MM"
            style={{ width: '100%' }}
            showNow={false}
            needConfirm={false}
            disabledTime={() => {
              if (
                submissionDeadline &&
                dayjs(submissionDeadline).isSame(dayjs(), 'day')
              ) {
                const now = dayjs();
                return {
                  disabledHours: () => {
                    const hours = [];
                    for (let i = 0; i < now.hour(); i++) {
                      hours.push(i);
                    }
                    return hours;
                  },
                  disabledMinutes: (selectedHour) => {
                    if (selectedHour === now.hour()) {
                      const minutes = [];
                      for (let i = 0; i < now.minute(); i++) {
                        minutes.push(i);
                      }
                      return minutes;
                    }
                    return [];
                  },
                };
              }
              return {};
            }}
          />
        </Form.Item>

        <Form.Item
          label="Review Dead Line"
          name="review_deadline"
          rules={[
            {
              required: true,
              message: 'Please select review deadline',
            },
          ]}
        >
          <DatePicker
            picker="date"
            format="YYYY/MM/DD"
            placeholder="YYYY/MM/DD"
            style={{ width: '100%' }}
            disabledDate={(current) => {
              if (submissionDeadline) {
                return (
                  current && current < dayjs(submissionDeadline).startOf('day')
                );
              }
              return current && current < dayjs().startOf('day');
            }}
          />
        </Form.Item>

        <div className="col-span-2">
          <div className="justify-start text-white text-lg font-normal">
            User Assigned
          </div>

          <div className="my-3 grid grid-cols-2 md:grid-cols-3 gap-3  mt-5">
            {currentApplicants?.map((emp, i) => {
              return (
                <Flex
                  key={i}
                  gap={10}
                  align="start"
                  className="p-2.5 rounded-lg bg-[#1E293B] relative group"
                >
                  <button
                    type="button"
                    onClick={() => handleRemove(emp.id)}
                    className="absolute -right-1.5 -top-1.5 p-1 bg-theme-primary rounded-full z-10"
                  >
                    <XIcon size={12} strokeWidth={3} className="text-white" />
                  </button>
                  <Space>
                    <CustomImage
                      alt="User"
                      width={25}
                      height={25}
                      src={IMAGES.avatar}
                      className="rounded-full flex-shrink-0"
                    />
                  </Space>
                  <div className="flex flex-col gap-1 overflow-hidden min-w-0">
                    <Space>
                      <div className="text-center justify-start text-white text-base font-medium truncate">
                        {emp.applicant_name}
                      </div>
                    </Space>
                    <Space size={8}>
                      <MailIcon size={12} className="text-white" />
                      <div className="justify-start text-white text-xs font-normal truncate">
                        {emp.email}
                      </div>
                    </Space>
                    <Space size={8} className="w-full">
                      <BriefcaseIcon size={12} className="text-theme-primary" />
                      <div className="text-white text-xs font-normal w-40 truncate">
                        {emp?.job_title || 'N/A'}
                      </div>
                    </Space>
                    <Space size={8} className="w-full">
                      <UserCogIcon size={12} className="text-theme-primary" />
                      <div className="text-white text-xs font-normal w-40 truncate">
                        {emp?.designation_name || 'N/A'}
                      </div>
                    </Space>
                  </div>
                </Flex>
              );
            })}
          </div>
        </div>

        <div className="col-span-2">
          <div className="flex justify-end">
            <Button
              className="btn-primary min-w-20"
              htmlType="submit"
              loading={isAssigning}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
}
